import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { RestockLotesService } from '../services/restock-lotes.service';
import { RestockDialogComponent } from './restock-dialog/restock-dialog.component';

const MY_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-produccion',
  templateUrl: './produccion.component.html',
  styleUrls: ['./produccion.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class ProduccionComponent implements OnInit {
  filtroForm: FormGroup;
  lotes: any[] = [];
  isLoading = false;
  displayedColumns = ['fecha', 'tipoProduccion', 'productos', 'costoTotal', 'unidades', 'gananciaPotencial'];

  tipoProduccionLabels: Record<string, string> = {
    impresion_3d: 'Impresión 3D',
    te: 'Té',
    manual: 'Manual'
  };

  constructor(
    private fb: FormBuilder,
    private restockLotesService: RestockLotesService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.filtroForm = this.fb.group({
      startDate: [moment().startOf('month').toDate()],
      endDate: [moment().endOf('month').toDate()]
    });
    this.cargarLotes();
  }

  cargarLotes(): void {
    const { startDate, endDate } = this.filtroForm.value;
    if (!startDate || !endDate) return;

    this.isLoading = true;
    const start = moment(startDate).startOf('day').toISOString();
    const end = moment(endDate).endOf('day').toISOString();

    this.restockLotesService.obtenerReporte(start, end).subscribe({
      next: (data) => {
        this.lotes = data || [];
        this.isLoading = false;
      },
      error: () => {
        this.toastr.error('Error al cargar el historial de producción');
        this.isLoading = false;
      }
    });
  }

  abrirNuevaProduccion(): void {
    const dialogRef = this.dialog.open(RestockDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      disableClose: false
    });
    dialogRef.afterClosed().subscribe((success: boolean) => {
      if (success) this.cargarLotes();
    });
  }

  get totalGasto(): number {
    return this.lotes.reduce((sum, l) => sum + (Number(l.costoTotalMaterial) || 0), 0);
  }

  get totalGanancia(): number {
    return this.lotes.reduce((sum, l) => sum + (Number(l.gananciaPotencial) || 0), 0);
  }

  get totalUnidades(): number {
    return this.lotes.reduce((sum, l) => sum + (Number(l.totalUnidades) || 0), 0);
  }

  exportarPdf(): void {
    if (!this.lotes.length) {
      this.toastr.warning('No hay datos para exportar');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const { startDate, endDate } = this.filtroForm.value;
    const title = `Historial de Producción — ${moment(startDate).format('DD/MM/YYYY')} al ${moment(endDate).format('DD/MM/YYYY')}`;
    doc.setFontSize(14);
    doc.text(title, 40, 40);

    autoTable(doc, {
      startY: 60,
      head: [['Fecha', 'Tipo', 'Productos', 'Costo Total', 'Unidades', 'Ganancia Potencial', 'Margen Bruto']],
      body: this.lotes.map(l => [
        moment(l.fecha).format('DD/MM/YYYY'),
        this.tipoProduccionLabels[l.tipoProduccion] || l.tipoProduccion,
        (l.items || []).map((i: any) => i.producto?.nombre || '').join(', '),
        `$${Number(l.costoTotalMaterial).toFixed(2)}`,
        l.totalUnidades,
        `$${Number(l.gananciaPotencial).toFixed(2)}`,
        `$${Number(l.margenBruto).toFixed(2)}`
      ]),
      theme: 'grid',
      headStyles: { fillColor: [63, 81, 181], textColor: 255 },
    });

    doc.save(`produccion-${moment().format('YYYYMMDD')}.pdf`);
    this.toastr.success('PDF generado');
  }
}
